<template>
  <div ref="saveButton" class="save-button" @click.stop.prevent="onClick">
    <slot
      :isSaved="view.isDocInAnyList"
      :isBusy="isBusy"
      :icon="icon"
      :label="label"
    >
      <button
        class="save-button__button"
        :class="{ 'save-button__button--saved': view.isDocInAnyList }"
        :aria-label="label"
        :aria-busy="isBusy"
        :disabled="isBusy"
      >
        <span
          class="save-button__icon iconfont"
          :class="icon"
          aria-hidden="true"
        />
      </button>
    </slot>

    <!-- Teleport so the popup isn't stacked beneath other cards' badges. -->
    <Teleport to="#appRoot">
      <AddToListPopup
        ref="destinationMenu"
        :view="view"
        :newStyle20321B="true"
        :anchor="saveButton"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { Raw } from 'vue'
import { useWait } from '@/app/composables/useWait'
import { DocCustomListsView } from '@/models/doc.custom.lists'
import type { DocsView, DocView } from '@/models/doc'
import type { UserDocListItem } from '@/models/interfaces'
import { Sentry } from '@/services/sentry'
import { appNotifications } from '@/services/notification.app'
import AddToListPopup from '@/app/components/doc/AddToListPopup.vue'

/**
 * The Save button for Save-for-Later.
 *
 * On the first click, if the doc is unsaved, save it to Want to Read.
 * If it is saved, open the destination menu.
 */
const props = defineProps<{
  /**
   * The doc to save/unsave.
   * Single doc pages pass DocView and list items pass UserDocListItem.
   */
  doc: UserDocListItem | DocView
  /**
   * The listing page's view, if any.
   */
  docsView?: DocsView
}>()

const wait = useWait()
const view = ref<Raw<DocCustomListsView>>(
  new DocCustomListsView(props.doc, wait, props.docsView),
)
// The reading page replaces the DocView when a different guide is opened.
// This component stays mounted, so update here with the listsView too.
watch(
  () => props.doc,
  (doc) => {
    view.value = new DocCustomListsView(doc, wait, props.docsView)
    dismissSaveConfirmation()
  },
)
const isBusy = ref(false)

const icon = computed(() => (view.value.isDocInAnyList ? 'iflag2' : 'iflag'))
const label = computed(() => (view.value.isDocInAnyList ? 'Saved' : 'Save'))
const destinationMenu = ref<InstanceType<typeof AddToListPopup> | null>(null)
const saveButton = ref<HTMLElement | null>(null)

let confirmationId: number | null = null

function dismissSaveConfirmation(): void {
  if (confirmationId !== null) {
    appNotifications.dismiss(confirmationId)
    confirmationId = null
  }
}

async function openDestinationMenu(): Promise<void> {
  // The confirmation would cover the menu, so dismiss it.
  dismissSaveConfirmation()

  // The destination menu waits for its list data to load before it opens, so
  // we wait for it. Show the button as busy in the meantime.
  isBusy.value = true
  try {
    await destinationMenu.value?.show()
  } finally {
    isBusy.value = false
  }
}

async function onClick(): Promise<void> {
  if (isBusy.value) {
    return
  }

  // Saved items open the destination menu; only unsaved ones are saved.
  if (view.value.isDocInAnyList) {
    await openDestinationMenu()
    return
  }

  isBusy.value = true
  const savedView = view.value
  try {
    await savedView.setInWantToRead(true)
    // If the user switched docs while the save was happening, don't show a
    // confirmation. (If we showed it, "Change" would act on the new doc
    // instead.)
    if (view.value !== savedView) {
      return
    }
    confirmationId = appNotifications.unique({
      message: 'Saved to Want to Read',
      compact: true,
      actions: [{ label: 'Change', onClick: openDestinationMenu }],
    })
  } catch (error) {
    Sentry.captureException(error)
  } finally {
    isBusy.value = false
  }
}
</script>

<style lang="scss" scoped>
.save-button {
  // The destination menu is positioned next to this element, so constrain it
  // to only be as wide as the button.
  width: fit-content;

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid rgba($c-black-3, 0.2);
    border-radius: 50%;
    background: $c-white;
    color: $c-black-3;
    cursor: pointer;

    &--saved {
      border-color: rgba($c-yellow, 0.3);
      background: $c-yellow-2;
      color: $c-yellow;
    }

    &[aria-busy='true'] {
      cursor: default;
      opacity: 0.7;
    }
  }

  &__icon {
    font-size: 16px;
    transform: translateY(1px);
  }
}

.night-mode .save-button__button {
  border-color: rgba($c-dark-4, 0.2);
  background: rgba($c-dark-1, 0.7);
  color: $c-dark-4;

  &--saved {
    border-color: $c-yellow;
    color: $c-yellow;
  }
}
</style>
